import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { upSertUom, UOM, fetchUoms, deleteUom } from '../../api'; // Adjust the import path as needed
import { Content } from '../../../_myb/layout/components/content';
import { ToolbarContext, defaultToolbarState } from '../../../_myb/layout/components/toolbar/Toolbar';
import { Column } from 'react-table';
import { KTIcon } from '../../../_myb/helpers';
import { DataTable } from '../widgets/components/DataTable';
import CustomModal from '../widgets/components/CustomModal';
import { toast } from 'react-toastify';
import { generateShortUUID } from './Utils';


interface UOMFormProps {
  onAddNew?: (newOption: UOM) => void;
  existingValue?: UOM;
}

export const UOMForm: React.FC<UOMFormProps> = ({ onAddNew, existingValue }) => {
  const [uom, setUom] = useState<UOM>(
    existingValue || { uomId: generateShortUUID(), uomNm: '', uomTxt: '' }
  );
  

  useEffect(() => {
   if(existingValue) setUom(existingValue);
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setUom(prevUom => ({
      ...prevUom,
      [name]: value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const updatedUom = await upSertUom(uom);
      toast.success('UOM saved successfully!'.toTranslatedDisplay());
      if(onAddNew) onAddNew(uom); // Pass the new or updated UOM to the parent component
    } catch (error) {
      toast.error('Error saving UOM.'.toTranslatedDisplay());
      console.error('Error upserting UOM:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label">{'UOM Name'.toTranslatedDisplay()}</label>
        <input
          type="text"
          className="form-control"
          placeholder={'Unit of Measure Name'.toTranslatedDisplay()}
          name="uomNm"
          value={uom.uomNm}
          onChange={handleChange}
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label">{'UOM Description'.toTranslatedDisplay()}</label>
        <input
          type="text"
          className="form-control"
          placeholder={'Unit of Measure Description'.toTranslatedDisplay()}
          name="uomTxt"
          value={uom.uomTxt}
          onChange={handleChange}
        />
      </div>

      <button type="submit" className="btn btn-primary mt-3">{'Save'.toTranslatedDisplay()}</button>
    </form>
  );
};


export const UOMList: React.FC = () => {
  const [uomList, setUomList] = useState<UOM[]>([]);
  const [uomForEdit, setUomForEdit] = useState<UOM>();
  const { updateToolbar } = useContext(ToolbarContext);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const updateButtons = () => {
    updateToolbar({
      ...defaultToolbarState,
      primaryButton: {
        enabled: true,
        text: 'Create UOM',
        icon: 'create', // Replace with appropriate icon type
        action: async () => {
          setUomForEdit(undefined);
          setIsEditMode(false);
          setIsModalOpen(true);
        }
      }
    });
  };

  useEffect(() => {
    updateButtons();
    const getUOMs = async () => {
      const fetchedUoms: UOM[] = await fetchUoms()
      setUomList(fetchedUoms);
    };
    getUOMs();
  }, [triggerRefresh, isModalOpen]);

  const handleEditUOM = async (uom: UOM) => {
    setUomForEdit(uom);
    setIsEditMode(true);
    setIsModalOpen(true);
  };
  const handleDeleteUom = async (UomId: string) => {
    try {
      const result = await deleteUom(UomId); // Call the API handler
      setTriggerRefresh(prev => !prev);
      toast.success(result || 'Uom deleted successfully!'.toTranslatedDisplay()); // Show success toast

    } catch (error: any) {
      toast.error(error.message || 'Failed to delete Uom.'.toTranslatedDisplay()); // Show error toast
    }
  };

  const columns: Column<UOM>[] = React.useMemo(
    () => [
      {
        Header: 'Name'.toTranslatedDisplay(),
        accessor: 'uomNm',
        size: 'small'
      },
      {
        Header: 'Text'.toTranslatedDisplay(),
        accessor: 'uomTxt',
        size: 'small'
      },
      {
        Header: 'Actions'.toTranslatedDisplay(),
        disableSortBy: true,
        width: '15%',
        ClassName: 'text-center',
        Cell: ({ row }: { row: { original: UOM } }) => (
          <div>
            <button
              onClick={() => {
                handleEditUOM(row.original);
              }}
              className="btn btn-sm btn-light p-2 m-1"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={'Edit'.toTranslatedDisplay()}
            >
              <KTIcon className="fs-3 text-hover-primary text-muted" iconType="solid" iconName="pencil" />
            </button>
            <button onClick={() => handleDeleteUom(row.original.uomId||'')} className="btn btn-sm btn-light p-2 m-1" data-bs-toggle="tooltip" data-bs-placement="top" title={'Delete'.toTranslatedDisplay()}>
              <KTIcon className="fs-3 text-muted text-hover-danger" iconName='trash' />
            </button>
          </div>
        )
      }
    ],
    []
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  function handleAddNew(newOption: UOM): void {
    setIsModalOpen(false);
    setTriggerRefresh(true);
  }

  return (
    <Content>
      <DataTable columns={columns} data={uomList}
        emptyStateActions={[
          { label: 'Create UOM'.toTranslatedDisplay(), onClick: () => { setUomForEdit(undefined); setIsEditMode(false); setIsModalOpen(true); } },
        ]} />
      <CustomModal
        title={`${isEditMode ? 'Edit'.toTranslatedDisplay() : 'Add'.toTranslatedDisplay()} UOM`}
        isVisible={isModalOpen}
        onClose={handleModalClose}
        body={
          <UOMForm onAddNew={function (newOption: UOM): void {
            setIsModalOpen(false)
          } }    
          existingValue={uomForEdit}        
          />
        }
        id=""
        onSave={() => {
          setIsModalOpen(false);
        }}
      />
    </Content>
  );
};


